import { CompanyInterface } from "interfaces/Company";

// One-time self-serve reactivation for companies the expiration cron locked out.
// There is no dedicated column: the whole state machine is derived from the
// approval status, and the marker below (appended to expiration_reason by
// POST /second-chance) is what makes it one-time. Keep these helpers as the single
// source of truth so the nav, the route guards and the expired card can't disagree.
export const SECOND_CHANCE_MARKER = "(Second Chance)";

const DAY_MS = 86400000;

// expirationDate parses as midnight UTC of the deadline day, but access runs
// through the END of that day — grantSecondChance compares against T23:59:59Z.
// Compare against midnight of the NEXT day so the button never shows while the
// API would still refuse it (and the grace period doesn't end a day early).
const expirationEndOf = (company?: CompanyInterface | null): Date | null => {
    const raw = company?.approvalStatus?.expirationDate;
    if (!raw) return null;
    const date = raw instanceof Date ? raw : new Date(raw);
    return isNaN(date.getTime()) ? null : new Date(date.getTime() + DAY_MS);
};

const hasMarker = (company?: CompanyInterface | null): boolean =>
    (company?.approvalStatus?.expirationReason || "").includes(SECOND_CHANCE_MARKER);

/** Portal access has lapsed: not approved and the deadline is behind us. */
export const isExpired = (company?: CompanyInterface | null): boolean => {
    if (!company || company.approved) return false;
    const expirationEnd = expirationEndOf(company);
    return !!expirationEnd && expirationEnd < new Date();
};

/** Expired and hasn't spent its one reactivation yet — show them the button. */
export const canTakeSecondChance = (company?: CompanyInterface | null): boolean =>
    isExpired(company) && !hasMarker(company);

/** Mid-grace-period: reactivated, deadline still ahead, not yet re-approved. */
export const isInSecondChance = (company?: CompanyInterface | null): boolean => {
    if (!company || company.approved || !hasMarker(company)) return false;
    const expirationEnd = expirationEndOf(company);
    return !!expirationEnd && expirationEnd >= new Date();
};

/**
 * Bucket A owes nothing but a signature — no qualifying purchase — so without this
 * they could reactivate, walk straight to the catalog and ignore the agreement for
 * another month. During their grace period the transactional surface (products,
 * orders, documents, checkout) is closed; Home + My Company stay open so they can
 * still finish account setup. B/C keep product access: their approval path includes
 * an actual purchase.
 */
export const secondChanceLockdown = (company?: CompanyInterface | null): boolean =>
    isInSecondChance(company) && company?.recontractBucket === "A";

/** Routes closed by the lockdown above. Matched as path prefixes. */
export const LOCKED_PATHS = ["/products", "/order-history", "/pending-orders", "/checkout"];

export const isLockedPath = (pathname: string): boolean =>
    LOCKED_PATHS.some((locked) => pathname === locked || pathname.startsWith(locked + "/"));
